/**
 * Contractor Performance Detail Page
 * ===================================
 * Detailed performance breakdown for a specific contractor.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, Clock, FileText, CheckCircle2, AlertTriangle, 
  TrendingUp, DollarSign, Users, Calendar, Phone, Mail,
  ThumbsUp, ThumbsDown, Minus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Tier colors
const TIER_COLORS = {
  'A+': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'A': 'bg-green-100 text-green-800 border-green-200',
  'B': 'bg-blue-100 text-blue-800 border-blue-200',
  'C': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'D': 'bg-red-100 text-red-800 border-red-200',
};

// Period options
const PERIODS = [
  { value: 'rolling_30', label: 'Last 30 Days' },
  { value: 'rolling_90', label: 'Last 90 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'Quarter' },
];

export default function ContractorPerformanceDetailPage() {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('rolling_30');
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/api/performance/contractors/${contractorId}?period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.ok) {
        setData(res.data);
      }
    } catch (err) {
      console.error('Error fetching contractor performance:', err);
      toast.error('Failed to load contractor performance');
    } finally {
      setLoading(false);
    }
  }, [contractorId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600">Contractor not found</p>
          <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </Card>
      </div>
    );
  }

  const contractor = data.contractor || {};
  const performance = data.performance || {};
  const recentJobs = data.recent_jobs || [];
  const escalations = data.escalation_history || [];
  const scores = performance.scores || {};
  const subScores = scores.sub_scores || {};
  const metrics = performance.metrics || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-performance-detail">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{contractor.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  {contractor.email && (
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      {contractor.email}
                    </span>
                  )}
                  {contractor.phone && (
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      {contractor.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white"
              >
                {PERIODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        
        {/* Score Header */}
        <div className="grid md:grid-cols-4 gap-6">
          {/* Total Score */}
          <Card className="md:col-span-1">
            <CardContent className="pt-6 text-center">
              <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full text-4xl font-bold ${
                performance.total_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                performance.total_score >= 60 ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {performance.total_score?.toFixed(0) || 'N/A'}
              </div>
              <div className="mt-4">
                <Badge className={`${TIER_COLORS[performance.tier] || 'bg-slate-100'} border text-lg px-4 py-1`}>
                  Tier {performance.tier || 'N/A'}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                Based on {performance.sample_size || 0} jobs
              </p>
              {performance.confidence_level === 'low' && (
                <p className="text-xs text-amber-600 mt-1">Low sample size</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="md:col-span-3">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatBox 
                  label="Jobs Assigned" 
                  value={performance.jobs_assigned || 0}
                  icon={FileText}
                />
                <StatBox 
                  label="Jobs Completed" 
                  value={performance.jobs_completed || 0}
                  icon={CheckCircle2}
                  color="green"
                />
                <StatBox 
                  label="Avg Response" 
                  value={metrics.response?.avg_hours ? `${metrics.response.avg_hours.toFixed(1)}h` : 'N/A'}
                  icon={Clock}
                  color={metrics.response?.avg_hours <= 6 ? 'green' : metrics.response?.avg_hours <= 24 ? 'amber' : 'red'}
                />
                <StatBox 
                  label="Avg Quote" 
                  value={metrics.quote?.avg_hours ? `${metrics.quote.avg_hours.toFixed(1)}h` : 'N/A'}
                  icon={FileText}
                  color={metrics.quote?.avg_hours <= 24 ? 'green' : metrics.quote?.avg_hours <= 48 ? 'amber' : 'red'}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sub-Scores Grid */}
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Performance Breakdown</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <SubScoreCard 
              label="Response" 
              score={subScores.response} 
              detail={metrics.response?.avg_hours ? `${metrics.response.avg_hours.toFixed(1)}h avg` : null}
              icon={Clock}
            />
            <SubScoreCard 
              label="Quote" 
              score={subScores.quote} 
              detail={metrics.quote?.within_24h_percent ? `${metrics.quote.within_24h_percent.toFixed(0)}% < 24h` : null}
              icon={FileText}
            />
            <SubScoreCard 
              label="Completion" 
              score={subScores.completion} 
              detail={metrics.completion?.on_time_percent ? `${metrics.completion.on_time_percent.toFixed(0)}% on time` : null}
              icon={CheckCircle2}
            />
            <SubScoreCard 
              label="Documentation" 
              score={subScores.documentation} 
              detail={metrics.documentation?.compliance_rate ? `${metrics.documentation.compliance_rate.toFixed(0)}% compliant` : null}
              icon={FileText}
            />
            <SubScoreCard 
              label="Certificates" 
              score={subScores.certificate} 
              detail={metrics.certificate?.not_applicable ? 'N/A' : metrics.certificate?.compliance_rate ? `${metrics.certificate.compliance_rate.toFixed(0)}%` : null}
              icon={CheckCircle2}
            />
            <SubScoreCard 
              label="Invoice" 
              score={subScores.invoice} 
              detail={metrics.invoice?.accuracy_rate ? `${metrics.invoice.accuracy_rate.toFixed(0)}% accurate` : null}
              icon={DollarSign}
            />
            <SubScoreCard 
              label="Uplift" 
              score={subScores.uplift} 
              detail={metrics.uplift?.uplift_request_rate ? `${metrics.uplift.uplift_request_rate.toFixed(0)}% rate` : null}
              icon={TrendingUp}
            />
            <SubScoreCard 
              label="Attendance" 
              score={subScores.attendance} 
              detail={metrics.attendance?.reliability_rate ? `${metrics.attendance.reliability_rate.toFixed(0)}% reliable` : null}
              icon={Calendar}
            />
            <SubScoreCard 
              label="Efficiency" 
              score={subScores.efficiency} 
              detail={metrics.efficiency?.grouped_jobs_rate ? `${metrics.efficiency.grouped_jobs_rate.toFixed(0)}% grouped` : null}
              icon={Users}
            />
          </div>
        </section>

        {/* Strengths & Improvement Areas */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Strengths */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ThumbsUp className="h-5 w-5 text-emerald-600" />
                Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              {performance.strengths?.length > 0 ? (
                <ul className="space-y-2">
                  {performance.strengths.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{s}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">No specific strengths identified</p>
              )}
            </CardContent>
          </Card>

          {/* Improvement Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ThumbsDown className="h-5 w-5 text-amber-600" />
                Areas for Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              {performance.improvement_areas?.length > 0 ? (
                <ul className="space-y-2">
                  {performance.improvement_areas.map((a, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{a}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">No improvement areas identified</p>
              )}

              {/* Risk Flags */}
              {performance.risk_flags?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-medium text-red-600 mb-2">Risk Flags</p>
                  <div className="flex flex-wrap gap-2">
                    {performance.risk_flags.map((flag, idx) => (
                      <Badge key={idx} className="bg-red-100 text-red-700 border-red-200 border text-xs">
                        {flag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Jobs Table */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Recent Jobs ({recentJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Job ID</th>
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Site</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Status</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Created</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Due</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentJobs.length > 0 ? recentJobs.map((job, idx) => (
                      <tr key={job.job_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2 font-mono text-xs text-slate-600">
                          {job.job_reference || job.job_id}
                        </td>
                        <td className="py-3 px-2 text-slate-800">{job.site_name || '-'}</td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={`${
                            job.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            job.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          } border text-xs`}>
                            {job.contractor_status || job.status || 'Unknown'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center text-slate-600">
                          {job.created_at ? new Date(job.created_at).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="py-3 px-2 text-center text-slate-600">
                          {job.due_date ? new Date(job.due_date).toLocaleDateString('en-GB') : '-'}
                        </td>
                        <td className="py-3 px-2 text-center">
                          {job.completed_at ? (
                            <span className="text-green-600">
                              {new Date(job.completed_at).toLocaleDateString('en-GB')}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-slate-500">
                          No recent jobs
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Escalation History */}
        {escalations.length > 0 && (
          <section>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Escalation History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {escalations.slice(0, 10).map((esc, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800">{esc.message || esc.escalation_type}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {esc.created_at ? new Date(esc.created_at).toLocaleString('en-GB') : '-'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

      </div>
    </div>
  );
}

// Stat Box Component
function StatBox({ label, value, icon: Icon, color }) {
  const colors = {
    green: 'text-emerald-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  };

  return (
    <div className="text-center p-4 bg-slate-50 rounded-xl">
      <Icon className="h-5 w-5 mx-auto mb-2 text-slate-400" />
      <div className={`text-2xl font-bold ${colors[color] || 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

// Sub-Score Card Component
function SubScoreCard({ label, score, detail, icon: Icon }) {
  const getScoreColor = (s) => {
    if (s >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (s >= 60) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-red-100 text-red-700 border-red-200';
  };

  return (
    <div className={`rounded-2xl border p-4 ${score != null ? getScoreColor(score) : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 opacity-70" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">{score != null ? score.toFixed(0) : 'N/A'}</div>
      {detail && <p className="text-xs mt-1 opacity-80">{detail}</p>}
    </div>
  );
}
